import { useEffect, useRef, useState } from 'react';
import type { ColumnConfig } from './ShowHideColumnsComponent.model';

/**
 * Custom hook to synchronize local column state with prop changes.
 *
 * Behavior depends on drag-and-drop mode:
 *
 * **Non-drag mode** (enableDragAndDrop = false):
 * - Always syncs directly with the columns prop
 * - Any prop change immediately updates local state
 *
 * **Drag-and-drop mode** (enableDragAndDrop = true):
 * - Preserves user's custom column order after drag operations
 * - Syncs when structural changes are detected:
 *   1. Column count changes (added/removed columns)
 *   2. Column identifiers change order in props (external reorder)
 * - When no structural change, syncs all column properties while preserving order
 *   (visible, alwaysVisible, dataTestId, etc.)
 *
 * This prevents the local reordered state from being overwritten
 * by parent re-renders while allowing property updates to propagate.
 *
 * @param columns - Column configuration from parent component
 * @param enableDragAndDrop - Whether drag-and-drop reordering is enabled
 * @returns Synchronized column state [localColumns, setLocalColumns]
 */
export const useColumnSync = (
  columns: ColumnConfig[],
  enableDragAndDrop: boolean
): [ColumnConfig[], React.Dispatch<React.SetStateAction<ColumnConfig[]>>] => {
  const [localColumns, setLocalColumns] = useState(columns);
  // Track the last prop order to detect external reorders
  // Compares incoming prop order vs previous prop order (not vs local state)
  const lastPropOrderRef = useRef<string[]>(
    columns.map((col) => col.columnId || col.label)
  );

  useEffect(() => {
    const currentPropOrder = columns.map((col) => col.columnId || col.label);

    if (!enableDragAndDrop) {
      // Non-drag mode: Always sync directly with prop changes
      setLocalColumns(columns);
      lastPropOrderRef.current = currentPropOrder;
      return;
    }

    // Drag-and-drop mode: Intelligently sync while preserving user's order
    setLocalColumns((prevColumns) => {
      // Detect if columns were added or removed
      if (prevColumns.length !== columns.length) {
        // Structural change detected - reset to new column set
        lastPropOrderRef.current = currentPropOrder;
        return columns;
      }

      // Check if prop order changed compared to last prop order (not local order)
      // This prevents resetting when parent re-renders with unchanged prop order
      const hasExternalReorder = lastPropOrderRef.current.some(
        (id, idx) => id !== currentPropOrder[idx]
      );

      if (hasExternalReorder) {
        // External reorder detected - respect parent's new order
        lastPropOrderRef.current = currentPropOrder;
        return columns;
      }

      // Update ref to current prop order for next comparison
      lastPropOrderRef.current = currentPropOrder;

      // No structural changes - sync all properties while preserving user's order
      // Create a map of updated column properties by identifier
      const columnPropsMap = new Map(
        columns.map((c) => [c.columnId || c.label, c])
      );

      return prevColumns.map((col) => {
        const updatedCol = columnPropsMap.get(col.columnId || col.label);
        // If column exists in new props, use all its properties except preserve the order
        return updatedCol || col;
      });
    });
  }, [columns, enableDragAndDrop]);

  return [localColumns, setLocalColumns];
};
